import { getMetric, type GetMetricQueryParams } from '@gonfalon/openapi/operations/getMetric';
import { getMetricEventInstances } from '@gonfalon/openapi/operations/getMetricEventInstances';
import { getMetricGroup, type GetMetricGroupQueryParams } from '@gonfalon/openapi/operations/getMetricGroup';
import { getMetricGroups, type GetMetricGroupsQueryParams } from '@gonfalon/openapi/operations/getMetricGroups';
import { getMetrics, type GetMetricsQueryParams } from '@gonfalon/openapi/operations/getMetrics';
import { createQueryKeys } from '@lukemorales/query-key-factory';

import { reactQueryResponseAdapter } from '../internal/reactQueryResponseAdapter';

/**
 * @deprecated Use `createQueryOptions` with the OpenAPI operation directly instead.
 * See packages/rest-api/README.md for the recommended pattern.
 */
export const metrics = createQueryKeys('metrics', {
  list: ({ projectKey, params }: { projectKey: string; params?: GetMetricsQueryParams }) => ({
    queryKey: params ? [projectKey, params] : [projectKey],
    queryFn: async () => reactQueryResponseAdapter(getMetrics({ projectKey, query: params })),
  }),
  detail: ({
    projectKey,
    metricKey,
    params,
  }: {
    projectKey: string;
    metricKey: string;
    params?: GetMetricQueryParams;
  }) => ({
    queryKey: params ? [projectKey, metricKey, params] : [projectKey, metricKey],
    queryFn: async () => reactQueryResponseAdapter(getMetric({ projectKey, metricKey, query: params })),
  }),
  events: ({
    projectKey,
    environmentKey,
    metricKey,
    offset,
    limit,
    uptoTime,
  }: {
    projectKey: string;
    environmentKey: string;
    metricKey: string;
    offset?: number;
    limit?: number;
    uptoTime?: number;
  }) => ({
    queryKey: [projectKey, environmentKey, metricKey, offset, limit, uptoTime],
    queryFn: async ({ signal }) =>
      reactQueryResponseAdapter(
        getMetricEventInstances(
          { projectKey, metricKey, query: { offset, limit, uptoTime, environmentKey } },
          { signal },
        ),
      ),
  }),
});

export const metricsList = metrics.list;
export const metricsDetail = metrics.detail;
export const metricsEvents = metrics.events;

/**
 * @deprecated Use `createQueryOptions` with the OpenAPI operation directly instead.
 * See packages/rest-api/README.md for the recommended pattern.
 */
export const metricGroups = createQueryKeys('metric-groups', {
  list: ({ projectKey, params }: { projectKey: string; params?: GetMetricGroupsQueryParams }) => ({
    queryKey: params ? [projectKey, params] : [projectKey],
    queryFn: async () => reactQueryResponseAdapter(getMetricGroups({ projectKey, query: params })),
  }),
  detail: ({
    projectKey,
    metricGroupKey,
    params,
  }: {
    projectKey: string;
    metricGroupKey: string;
    params?: GetMetricGroupQueryParams;
  }) => ({
    queryKey: params ? [projectKey, metricGroupKey, params] : [projectKey, metricGroupKey],
    queryFn: async () => reactQueryResponseAdapter(getMetricGroup({ projectKey, metricGroupKey, query: params })),
  }),
});

export const metricGroupsList = metricGroups.list;
export const metricGroupsDetail = metricGroups.detail;

// extra query-options exports for metrics

import { createInfiniteQueryOptions } from '../internal/createInfiniteQueryOptions';
import { createQueryOptions } from '../internal/createQueryOptions';

export const getMetricsQuery = createQueryOptions(getMetrics);
export const getInfiniteMetricsQuery = createInfiniteQueryOptions(getMetrics);
export const getMetricQuery = createQueryOptions(getMetric);

const getMetricDetailsExpand = (): NonNullable<GetMetricQueryParams['expand']> => {
  return [
    'metricGroups',
    'experiments',
    'experimentCount',
    'metricGroupCount',
    'eventSources',
    'guardedRollouts',
    'guardedRolloutCount',
    'releasePolicies',
    'releasePolicyCount',
  ] as NonNullable<GetMetricQueryParams['expand']>;
};

export const getMetricDetailsQuery = ({
  projectKey,
  metricKey,
  query = {
    expand: getMetricDetailsExpand(),
  },
}: {
  projectKey: string;
  metricKey: string;
  query?: GetMetricQueryParams;
}) => {
  return getMetricQuery({
    projectKey,
    metricKey,
    query,
  });
};
